<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { AssignmentType } from "~/types/enums/AssignmentType.enum";
import {
  DocumentGradedState,
  DocumentGradedStateOptions,
} from "~/types/enums/DocumentGradedState.enum";
import { DocumentState } from "~/types/enums/DocumentState";
import { ExportDocumentFormat } from "~/types/enums/ExportDocumentFormat.enum";
import { SubmittedDocumentBadge } from "~/types/SubmittedDocument";

const documentId = inject("documentId") as any;
const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { updateDocumentState } = submittedDocumentStore;
const { submittedDocument, revision, hasContacts } = storeToRefs(
  submittedDocumentStore
);

const userClassroomStore = useUserClassroomsDataStore();
const { allAssignments, classrooms } = storeToRefs(userClassroomStore);

const appUserDataStore = useAppUserDataStore();
const { commentColor } = storeToRefs(appUserDataStore);

const badgeOptions = [
  {
    label: "Gold",
    value: SubmittedDocumentBadge.gold,
    img: "/img/badges/gold_badge.png",
  },
  {
    label: "Silver",
    value: SubmittedDocumentBadge.silver,
    img: "/img/badges/silver_badge.png",
  },
  {
    label: "Bronze",
    value: SubmittedDocumentBadge.bronze,
    img: "/img/badges/bronze_badge.png",
  },
];

const calculateBadge = () => {
  if (submittedDocument.value?.enableSubmissionBadges != true) {
    return null;
  }

  let earned = 0;
  let total = 0;

  if (useChecklist.value) {
    earned = useTotalEarnedScore.value;
    total = useTotalScore.value;
  } else if (
    submittedDocument.value?.earnedPoints !== undefined &&
    submittedDocument.value?.maxPoints !== undefined
  ) {
    earned = submittedDocument.value.earnedPoints;
    total = submittedDocument.value.maxPoints;
  }

  if (total === 0) return null;

  const percentage = earned / total;

  if (percentage >= 0.9) return SubmittedDocumentBadge.gold;
  if (percentage >= 0.8) return SubmittedDocumentBadge.silver;
  return SubmittedDocumentBadge.bronze;
};

const updatingStatus = ref(false);
const updateGradedStatus = async () => {
  updatingStatus.value = true;

  const state =
    submittedDocument.value!.state == DocumentState.graded
      ? DocumentState.grading
      : DocumentState.graded;

  // Here we'll also lock in the ink color
  submittedDocument.value!.inkColor = commentColor.value;

  if (state == DocumentState.graded) {
    submittedDocument.value!.documentGradedState = DocumentGradedState.accepted;
    saveDocument();
  }

  submittedDocument.value!.state = state;
  await updateDocumentState(state);
  updatingStatus.value = false;
};

const saveDocument = async () => {
  await submittedDocumentStore.saveDocument();
};

const updateDocumentGradedState = (state: DocumentGradedState) => {
  if (submittedDocument.value == undefined) return;

  submittedDocument.value.documentGradedState = state;
  saveDocument();
};

const documentChecklistStore = useDocumentChecklistStore(documentId);

const { useChecklist, useTotalEarnedScore, useTotalScore, isOSS } = storeToRefs(
  documentChecklistStore
);

const exportFormats = computed(() => {
  if (isOSS.value == true) return [ExportDocumentFormat.docx];

  if (
    revision.value?.fileObject != undefined &&
    revision.value.fileObject.mimeType.includes("pdf")
  ) {
    return [ExportDocumentFormat.pdf];
  }

  return [
    ExportDocumentFormat.docx,
    ExportDocumentFormat.pdf,
    ExportDocumentFormat.clipboard,
  ];
});

const showGradingStateDropdown = computed(() => {
  if (submittedDocument.value == undefined) return false;

  // if (submittedDocument.value.state != DocumentState.graded) return false;

  return (
    submittedDocument.value.allowFinalDraftGradedState == true ||
    submittedDocument.value.documentSubmissionType ==
      DocumentSubmissionType.roughDraft ||
    submittedDocument.value.documentSubmissionType ==
      DocumentSubmissionType.keyWordOutline
  );
});

const matchingAssignment = computed(() => {
  return allAssignments.value.find(
    (a) => a.id == submittedDocument.value?.assignmentId
  );
});

const matchingClassroom = computed(() => {
  return classrooms.value?.find(
    (c) => c.id == submittedDocument.value?.classroomId
  );
});

const showManualPointsEntry = computed(() => {
  if (
    showGradingStateDropdown.value == true ||
    submittedDocument.value?.allowFinalDraftGradedState == true
  )
    return false;

  if (useChecklist.value != undefined) return false;

  if (matchingAssignment.value == undefined) return false;

  return matchingAssignment.value.type == AssignmentType.general;
});

const onUpdateScores = async () => {
  await saveDocument();
};

const showNotifyStudentButton = computed(() => {
  return hasContacts.value == true;
});

const isAutoReturnEnabled = computed(() => {
  return matchingAssignment.value?.isAutoReturnEnabled != undefined
    ? matchingAssignment.value.isAutoReturnEnabled
    : matchingClassroom.value?.isAutoReturnEnabled;
});

const toggleBadge = (badge: SubmittedDocumentBadge) => {
  if (submittedDocument.value!.badge === badge) {
    submittedDocument.value!.badge = undefined;
  } else {
    submittedDocument.value!.badge = badge;
  }
  saveDocument();
};

watch(
  () => submittedDocument.value?.state,
  async (newState, oldState) => {
    if (submittedDocument.value?.enableSubmissionBadges != true) {
      return;
    }

    if (
      newState === DocumentState.graded &&
      oldState !== DocumentState.graded &&
      !submittedDocument.value?.badge
    ) {
      const suggestedBadge = calculateBadge();
      if (suggestedBadge) {
        submittedDocument.value!.badge = suggestedBadge;
        await saveDocument();
      }
    }
  }
);
const gradedStateTabs = computed(() => {
  return DocumentGradedStateOptions.map((option) => ({
    key: option.value,
    label: option.label,
  }));
});

const canShowPoints = computed(() => {
  if (useChecklist.value == undefined) return false;

  if (
    submittedDocument.value?.documentSubmissionType ==
    DocumentSubmissionType.finalDraft
  )
    return true;

  if (
    submittedDocument.value?.documentSubmissionType ==
      DocumentSubmissionType.roughDraft &&
    submittedDocument.value?.assignmentRequireRoughDraftChecklist == true
  )
    return true;

  return false;
});
</script>

<template>
  <div
    data-step-id="graded-toggle"
    class="p-4 flex flex-col items-center border border-medium rounded transition-all duration-200"
    :class="{
      'bg-success-default bg-opacity-10 border-success-default':
        submittedDocument?.state == DocumentState.graded,
      'hover:bg-light-default':
        submittedDocument?.state != DocumentState.graded,
    }"
  >
    <!-- Header: Graded Toggle -->
    <div class="flex flex-row items-center justify-between w-full">
      <div class="flex flex-row items-center">
        <span class="mr-3 text-body-1 text-[20px] font-medium"> Graded </span>
        <BaseToggle
          :model-value="submittedDocument?.state == DocumentState.graded"
          @update:model-value="updateGradedStatus"
        />
      </div>

      <!-- Grading Controls (Shown on right when graded) -->
      <div class="flex flex-row items-center space-x-4">
        <!-- 1. Accepted / Incomplete Toggle -->
        <div
          v-if="submittedDocument && showGradingStateDropdown"
          class="flex flex-row items-center space-x-2"
        >
          <span v-if="canShowPoints == true" class="text-xs text-gray-500">
            ({{ useTotalEarnedScore }} / {{ useTotalScore }})
          </span>
          <BaseToggleButton
            :model-value="submittedDocument.documentGradedState"
            :tabs="gradedStateTabs"
            tab-width="100px"
            size="xs"
            @update:model-value="updateDocumentGradedState"
          />
        </div>

        <!-- 2. Manual Points Entry -->
        <div
          v-if="submittedDocument && showManualPointsEntry"
          class="flex flex-row items-center justify-center"
        >
          <BaseTextInput
            v-model.number="submittedDocument.earnedPoints"
            class="w-[50px] text-center font-bold"
            @update:model-value="onUpdateScores"
          />
          <span class="mx-1 text-gray-500"> / </span>
          <BaseTextInput
            v-model.number="submittedDocument.maxPoints"
            class="w-[50px] text-center font-bold"
            @update:model-value="onUpdateScores"
          />
        </div>

        <!-- 3. Score Display Only (if checklist used but no manual entry/dropdown) -->
        <div
          v-if="
            !showGradingStateDropdown && !showManualPointsEntry && useChecklist
          "
          class="flex flex-col items-center"
        >
          <div
            class="flex flex-row items-baseline px-3 py-1 border rounded border-success-default bg-success-default/10"
          >
            <span class="text-xl font-bold text-success-default leading-none">
              {{ useTotalEarnedScore }}
            </span>
            <span class="text-gray-500 text-sm ml-1 leading-none">
              / {{ useTotalScore }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <TransitionsCollapsible>
      <div
        v-if="submittedDocument?.state == DocumentState.graded"
        class="flex flex-col items-center justify-between w-full mt-4 border-t border-medium-tint"
      >
        <!-- Badge Selection -->
        <div
          v-if="submittedDocument.enableSubmissionBadges == true && submittedDocument.documentSubmissionType == DocumentSubmissionType.finalDraft"
          class="flex flex-row items-center justify-center space-x-6 w-full py-3 border-b border-medium-tint"
        >
          <div
            v-for="badge in badgeOptions"
            :key="badge.value"
            class="cursor-pointer transition-all duration-200 rounded p-1"
            :class="[
              submittedDocument.badge === badge.value
                ? 'ring-2 ring-success-default ring-offset-2 opacity-100'
                : 'opacity-40 hover:opacity-100',
            ]"
            @click="toggleBadge(badge.value)"
          >
            <img :src="badge.img" :alt="badge.label" class="w-8 h-8" />
          </div>
        </div>

        <!-- Action Buttons Row -->
        <div
          class="flex flex-row items-center justify-center space-x-4 w-full pt-2"
        >
          <!-- Return Button -->
          <div
            v-if="
              submittedDocument.classroomId != undefined &&
              isAutoReturnEnabled != true
            "
          >
            <BaseTextButton
              @click="
                submittedDocument.isReturnedToStudent =
                  !submittedDocument.isReturnedToStudent;
                saveDocument();
              "
              color="default"
            >
              <div class="flex flex-row items-center">
                <BaseCheckbox
                  :model-value="submittedDocument.isReturnedToStudent"
                  class="pointer-events-none mr-2"
                />
                <span>Return</span>
              </div>
            </BaseTextButton>
          </div>

          <!-- Export -->
          <ExportDocumentButton
            :formats="exportFormats"
            @click.stop.prevent
            variant="text"
            color="default"
          />

          <!-- Notify -->
          <DocumentNotifyStudentButton
            v-if="showNotifyStudentButton"
            :document-id="documentId"
            variant="text"
            color="default"
          />
        </div>
      </div>
    </TransitionsCollapsible>
  </div>
</template>

<style scoped></style>
